import { Link } from 'react-router-dom';

export function Landing() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            {/* Paper frame with scan line */}
            <div className="paper-frame p-8 md:p-12 mb-8 relative">
              <div className="scan-line" aria-hidden="true" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text mb-6 leading-tight display">
                Chụp ảnh hóa đơn → Lấy dữ liệu ngay
              </h1>
              <p className="text-lg md:text-xl text-text-muted mb-8 max-w-2xl mx-auto">
                Không nhập tay. AI vision trích xuất: tên công ty, MST, ngày, số tiền, thuế VAT.
                Xuất Excel, CSV, JSON hoặc sync Google Sheets.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register" className="btn-primary text-lg px-8 py-3 w-full sm:w-auto focus-ring">
                  Bắt đầu miễn phí
                </Link>
                <Link to="/pricing" className="btn-outline text-lg px-8 py-3 w-full sm:w-auto focus-ring">
                  Xem bảng giá
                </Link>
              </div>
              <p className="mt-4 text-sm text-text-muted">
                Không cần thẻ tín dụng · Dùng thử 7 ngày gói Pro · 2.000+ kế toán viên đang dùng
              </p>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-text-muted mt-10">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                Xử lý cục bộ trên máy
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                Gemini 2.5 · GPT-4o · Claude 3.5
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                Tích hợp Google Sheets
              </span>
            </div>
          </div>
        </div>

        {/* Decorative background elements - subtle */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>
      </section>

      {/* Features Section - Data field style */}
      <section className="py-20 bg-surface">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4 display">
              Trích xuất đủ các trường quan trọng
            </h2>
            <p className="text-lg text-text-muted max-w-2xl mx-auto">
              Mỗi hóa đơn được đọc thành dữ liệu có cấu trúc, sẵn sàng cho kế toán
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.field}
                className="feature-field"
                data-field={feature.field}
              >
                <div className="icon-wrapper">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - Pipeline style */}
      <section className="py-20 bg-bg">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4 display">
              3 bước để số hóa hóa đơn
            </h2>
            <p className="text-lg text-text-muted max-w-2xl mx-auto">
              Quy trình đơn giản, không cần training, chạy ngay trên desktop
            </p>
          </div>

          <div className="relative">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
              {steps.map((step, index) => (
                <div key={step.title} className="relative">
                  <div className="flex justify-center mb-6">
                    <div className="relative z-10">
                      <div className="w-20 h-20 rounded-full bg-surface border-2 border-brand flex items-center justify-center">
                        <span className="text-2xl font-bold text-brand display">{index + 1}</span>
                      </div>
                      {/* Pulse ring */}
                      <div className="absolute inset-0 rounded-full border-2 border-brand/30 animate-pulse" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="card p-6 h-full text-center">
                    <h3 className="text-xl font-semibold text-text mb-2 display">{step.title}</h3>
                    <p className="text-text-muted">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-brand text-white relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fillRule=%22evenodd%22%3E%3Cpath fill=%22%23ffffff%22 fillOpacity=%220.03%22 d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
        </div>
        <div className="container text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 display">
            Sẵn sàng giảm 85% thời gian nhập liệu?
          </h2>
          <p className="text-lg text-brand-light mb-8 max-w-2xl mx-auto">
            Tham gia 2.000+ kế toán viên đã tự động hóa quy trình hóa đơn với OCR Invoice
          </p>
          <Link to="/register" className="btn-accent text-lg px-8 py-3 focus-ring">
            Tạo tài khoản miễn phí
          </Link>
        </div>
      </section>
    </div>
  );
}

const features = [
  {
    field: 'TÊN CÔNG TY',
    title: 'Tên công ty',
    description: 'Trích xuất chính xác tên người bán/người mua từ hóa đơn điện tử',
    icon: function CompanyIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    },
  },
  {
    field: 'MÃ SỐ THUẾ',
    title: 'Mã số thuế (MST)',
    description: 'Nhận diện MST 10-13 chữ số, validate format chuẩn Việt Nam',
    icon: function TaxIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    },
  },
  {
    field: 'NGÀY HÓA ĐƠN',
    title: 'Ngày hóa đơn',
    description: 'Parse nhiều định dạng ngày: DD/MM/YYYY, YYYY-MM-DD, DD-MM-YY',
    icon: function DateIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    },
  },
  {
    field: 'TỔNG TIỀN',
    title: 'Tổng thanh toán',
    description: 'Tổng cộng, tiền hàng, thuế VAT, giảm trừ – tách riêng từng cột',
    icon: function AmountIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    },
  },
  {
    field: 'DÒNG MỤC',
    title: 'Chi tiết dòng mục',
    description: 'Tên hàng, đơn vị, số lượng, đơn giá, thành tiền – mỗi dòng một record',
    icon: function ItemsIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    },
  },
  {
    field: 'THUẾ VAT',
    title: 'Thuế GTGT',
    description: 'Tách biệt VAT 5%, 8%, 10%, 0% – đúng quy định hiện hành',
    icon: function TaxIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    },
  },
  {
    field: 'ĐỊA CHỈ',
    title: 'Địa chỉ công ty',
    description: 'Trích xuất địa chỉ đầy đủ, tách tỉnh/huyện/xã cho báo cáo',
    icon: function AddressIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    },
  },
  {
    field: 'SỐ HÓA ĐƠN',
    title: 'Ký hiệu & Số hóa đơn',
    description: 'Ký hiệu mẫu, số thứ tự, mã tra cứu – đầy đủ theo quy định',
    icon: function InvoiceIcon({ className }: { className?: string }) {
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    },
  },
];

const steps = [
  {
    title: 'Tải lên',
    description: 'Kéo thả ảnh/PDF hoặc chụp từ camera. Hỗ trợ nhiều file cùng lúc.',
  },
  {
    title: 'AI xử lý',
    description: 'Vision model trích xuất: công ty, MST, ngày, dòng mục, thuế, tổng tiền.',
  },
  {
    title: 'Xuất dữ liệu',
    description: 'Xem trước, sửa nếu cần, xuất Excel/CSV/JSON hoặc sync Google Sheets.',
  },
];